// /apps/patient/src/pages/appointments/AppointmentRequest.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { usePatient } from '../../contexts/PatientContext';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  AlertCircle, Send, Info
} from 'lucide-react';

import { FormDefinition, AppointmentTimeData } from '@prism-ehr/shared';

import { ProviderSelectField } from '@prism-ehr/shared';
import { LocationSelectField } from '@prism-ehr/shared';
import { AppointmentTimeField } from '@prism-ehr/shared';
import { readErrorMessage } from '../../components/appointments/manageTypes';

export function AppointmentRequest() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped when the server reports the slot was just taken: remounts the picker
  // so the patient re-picks a day and gets fresh slots.
  const [pickerNonce, setPickerNonce] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitResponse, setSubmitResponse] = useState<any>(null);

  // Scheduling form state
  const [schedulingForm, setSchedulingForm] = useState<FormDefinition | null>(null);
  const [orgSettings, setOrgSettings] = useState<any>({});
  const [orgLocations, setOrgLocations] = useState<any[]>([]);
  const { patient } = usePatient();
  const [patientData, setPatientData] = useState<any>(null);

  // Field visibility based on form
  const [showProviderField, setShowProviderField] = useState(false);
  const [showLocationField, setShowLocationField] = useState(false);
  const [showAppointmentTimeField, setShowAppointmentTimeField] = useState(true);

  // Form data
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [appointmentData, setAppointmentData] = useState<AppointmentTimeData | null>(null);
  const [visitReason, setVisitReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    loadSchedulingFormAndSettings();
  }, []);

  const loadSchedulingFormAndSettings = async () => {
    try {
      setLoading(true);

      // Get patient data first
      if (!patient) {
        throw new Error('Patient not found');
      }
      setPatientData(patient);

      // Get scheduling form configuration
      const form = await patientApiService.getSchedulingForm();
      if (form) {
        setSchedulingForm(form);

        // Determine which fields to show based on form questions
        const hasProviderField = form.form_version.definition.questions.some(q => q.type === 'providerSelect');
        const hasLocationField = form.form_version.definition.questions.some(q => q.type === 'locationSelect');

        setShowProviderField(hasProviderField);

        // Fetch org settings and locations from the organization API (not form.settings)
        const orgId = form.organization_id;
        let locationList: any[] = [];
        if (orgId) {
          try {
            const [org, locations] = await Promise.all([
              patientApiService.getOrganizationPublic(orgId),
              patientApiService.getOrganizationLocationsPublic(orgId)
            ]);
            setOrgSettings(org.settings || {});
            locationList = Array.isArray(locations) ? locations : [];
            setOrgLocations(locationList);
          } catch (orgErr) {
            console.error('Error loading org settings:', orgErr);
          }
        }

        // Multi-location orgs always get the location picker, regardless of
        // whether the scheduling form was authored with a locationSelect question.
        setShowLocationField(hasLocationField || locationList.length > 1);

        // Default the location when no picker is needed: the org's only
        // location first, then the form's own location.
        if (!hasLocationField && locationList.length <= 1) {
          if (locationList.length === 1) {
            setSelectedLocation(locationList[0].id);
          } else if (form.location_id) {
            setSelectedLocation(form.location_id);
          }
        }
      }
    } catch (err) {
      console.error('Error loading scheduling configuration:', err);
      setError('Failed to load appointment scheduling options');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!appointmentData) {
      setError('Please select an appointment time');
      return;
    }

    if (!orgSettings?.calendar_display?.hide_visit_reason && !visitReason.trim()) {
      setError('Please provide a reason for your visit');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const requestData: Record<string, any> = {
        provider_id: appointmentData.provider_id,
        location_id: appointmentData.location_id || selectedLocation,
        appointment_type: appointmentData.appointment_type,
        appointment_type_id: appointmentData.appointment_type_id,
        duration_minutes: appointmentData.duration_minutes,
        ...(orgSettings?.calendar_display?.hide_visit_reason ? {} : { visit_reason: visitReason }),
        additional_notes: additionalNotes,
        is_urgent: isUrgent,
        is_telehealth: appointmentData.is_telehealth || false,
        timezone: appointmentData.timezone,
      };

      // Include mode-specific data
      if (appointmentData.preferred_slots && appointmentData.preferred_slots.length > 0) {
        requestData.preferred_slots = appointmentData.preferred_slots;
      } else {
        requestData.appointment_date = appointmentData.appointment_date;
        requestData.start_time = appointmentData.start_time;
        requestData.end_time = appointmentData.end_time;
        requestData.start_timestamp = appointmentData.start_timestamp;
        requestData.end_timestamp = appointmentData.end_timestamp;
      }

      const response = await patientApiService.submitAppointmentRequest(requestData);
      setSubmitResponse(response);

      const confirmationType = response.mode === 'confirmed'
        ? 'Your appointment has been confirmed!'
        : 'Your appointment request has been submitted. We will contact you within 24 hours to confirm.';

      setSuccessMessage(confirmationType);

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/appointments');
      }, 3000);
    } catch (err) {
      console.error('Error submitting appointment:', err);
      const message = await readErrorMessage(err, 'Failed to submit appointment request. Please try again.');
      setError(message);
      if (/just taken/i.test(message)) {
        setAppointmentData(null);
        setPickerNonce(n => n + 1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAppointmentTimeChange = (data: AppointmentTimeData) => {
    setAppointmentData(data);

    // If provider was auto-selected (in aggregated mode), update the selected provider
    if (data.provider_id && !selectedProvider) {
      setSelectedProvider(data.provider_id);
    }
  };

  const handleProviderChange = (value: string | string[]) => {
    // Since we're not using allowMultiple, value will always be string
    setSelectedProvider(value as string);
  };

  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Request Appointment" showBack />
        <div className="p-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              {submitResponse?.mode === 'confirmed' ? 'Appointment Confirmed' : 'Request Submitted'}
            </h3>
            <p className="text-green-700">
              {successMessage}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Schedule Appointment" showBack />

      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Scheduling Mode Alert */}
        {orgSettings.scheduling_mode === 'require_approval' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium">Appointment Request</p>
                <p className="text-amber-700 text-sm mt-1">
                  This is an appointment request. Our team will review and contact you to confirm the final appointment time.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Location Selection (if enabled) */}
        {showLocationField && schedulingForm && (() => {
          const locationQuestion = schedulingForm.form_version.definition.questions.find(
            (q: any) => q.type === 'locationSelect'
          ) as any;
          const cfg = locationQuestion?.properties?.locationSelect || {};
          return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <LocationSelectField
                organizationId={schedulingForm.organization_id}
                value={selectedLocation}
                onChange={handleLocationChange}
                required={true}
                showAddress={cfg.showAddress ?? true}
                showDistance={cfg.showDistance ?? false}
                showMap={cfg.showMap ?? false}
                showProviders={cfg.showProviders ?? false}
                apiService={patientApiService}
              />
            </div>
          );
        })()}

        {/* Provider Selection (if enabled) */}
        {showProviderField && schedulingForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Provider
            </label>
            <ProviderSelectField
              organizationId={schedulingForm.organization_id}
              locationId={selectedLocation}
              value={selectedProvider}
              onChange={handleProviderChange}
              required={false}
              showSpecialty={true}
              showAvailability={false}
              filterByLocation={!!selectedLocation}
              allowMultiple={false}
            />
          </div>
        )}

        {/* Appointment Time Selection */}
        {schedulingForm && (() => {
          const apptQuestion = schedulingForm.form_version.definition.questions.find(
            (q: any) => q.type === 'appointmentTime'
          ) as any;
          const apptCfg = apptQuestion?.properties?.appointmentConfig || {};
          // If form builder hasn't set showProviderName explicitly, fall back to the
          // existing heuristic (hide provider names when there's no provider picker).
          const showProviderName = apptCfg.showProviderName ?? !showProviderField;
          return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <AppointmentTimeField
                key={`request-picker-${pickerNonce}`}
                form={schedulingForm}
                providerId={selectedProvider || undefined}
                locationId={selectedLocation || undefined}
                value={appointmentData}
                onChange={handleAppointmentTimeChange}
                showAppointmentTypes={apptCfg.showAppointmentTypes ?? true}
                showProviderName={showProviderName}
                required={true}
                apiService={patientApiService}
                bookingContext="portal"
                requireLocation={orgLocations.length > 1}
              />
            </div>
          );
        })()}

        {/* Reason for Visit */}
        {!orgSettings?.calendar_display?.hide_visit_reason && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Visit <span className="text-red-500">*</span>
            </label>
            <textarea
              value={visitReason}
              onChange={(e) => setVisitReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              rows={3}
              placeholder="Please describe your symptoms or reason for the appointment..."
              required
            />
          </div>
        )}

        {/* Additional Notes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes (Optional)
          </label>
          <textarea
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            rows={3}
            placeholder="Any additional information you'd like to share..."
          />
        </div>

        {/* Urgent Flag */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isUrgent}
              onChange={(e) => setIsUrgent(e.target.checked)}
              className="w-5 h-5 text-primary focus:ring-primary"
            />
            <span className="text-gray-700">This is an urgent request</span>
          </label>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-3 pt-4 pb-8">
          <button
            type="button"
            onClick={() => navigate('/appointments')}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            disabled={submitting || !appointmentData}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>
                  {orgSettings.scheduling_mode === 'auto_confirm' ? 'Book Appointment' : 'Request Appointment'}
                </span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}